import React from 'react';
import { motion } from 'framer-motion';
import { BrainCircuitIcon, CarIcon, HomeIcon, BriefcaseIcon, SettingsIcon, Trash2Icon, SparklesIcon, ShieldIcon } from 'lucide-react';
const preferences = [{
  category: 'Cars',
  icon: CarIcon,
  items: ['Toyota, Nissan, Lexus', 'Under AED 100,000', 'GCC Specs only', 'SUV or Sedan']
}, {
  category: 'Property',
  icon: HomeIcon,
  items: ['Dubai Marina, JLT, Al Barsha', 'Near Metro Station', '2-3 Bedrooms', 'Pet friendly']
}, {
  category: 'Jobs',
  icon: BriefcaseIcon,
  items: ['Marketing Manager', 'Remote or Hybrid', 'AED 20k+ Expected']
}];
const aiInsights = ['Prado prices dropped 5% this week in your preferred areas.', 'Your property budget may fit Arjan better than Al Barsha based on recent trends.', 'A new Marketing Manager role matches your experience perfectly.'];
export function MyAI() {
  return <div className="flex-1 p-4 lg:p-8 max-w-4xl mx-auto w-full" data-id="element-2778">
      <div className="flex items-center gap-4 mb-8" data-id="element-2779">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-lg" data-id="element-2780">
          <BrainCircuitIcon className="w-8 h-8 text-white" data-id="element-2781" />
        </div>
        <div data-id="element-2782">
          <h1 className="font-display font-bold text-3xl text-dark-navy mb-1" data-id="element-2783">
            My AI Memory
          </h1>
          <p className="text-slate-500" data-id="element-2784">
            What Talab knows about your preferences to give you better matches.
          </p>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-gradient-to-br from-primary-indigo/5 via-ai-purple/5 to-electric-cyan/5 rounded-3xl p-6 lg:p-8 border border-primary-indigo/10 ai-glow mb-8" data-id="element-2785">
        <div className="flex items-center gap-3 mb-6" data-id="element-2786">
          <SparklesIcon className="w-6 h-6 text-ai-purple" data-id="element-2787" />
          <h2 className="font-display font-bold text-xl text-dark-navy" data-id="element-2788">
            Latest Insights for You
          </h2>
        </div>
        <div className="space-y-4" data-id="element-2789">
          {aiInsights.map((insight, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: idx * 0.1
        }} className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm" data-id="element-2790">
              <p className="text-slate-700 font-medium" data-id="element-2791">{insight}</p>
            </motion.div>)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-2792">
        {/* Preferences */}
        <div className="space-y-6" data-id="element-2793">
          <h2 className="font-display font-bold text-xl text-dark-navy flex items-center gap-2" data-id="element-2794">
            <SettingsIcon className="w-5 h-5 text-slate-400" data-id="element-2795" />
            Learned Preferences
          </h2>

          <div className="space-y-4" data-id="element-2796">
            {preferences.map((pref, idx) => {
            const Icon = pref.icon;
            return <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-id="element-2797">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2798">
                    <div className="p-2 bg-slate-100 rounded-lg" data-id="element-2799">
                      <Icon className="w-5 h-5 text-slate-600" data-id="element-2800" />
                    </div>
                    <h3 className="font-bold text-slate-900" data-id="element-2801">
                      {pref.category}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-2802">
                    {pref.items.map((item, i) => <span key={i} className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-600" data-id="element-2803">
                        {item}
                      </span>)}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Privacy Controls */}
        <div className="space-y-6" data-id="element-2804">
          <h2 className="font-display font-bold text-xl text-dark-navy flex items-center gap-2" data-id="element-2805">
            <ShieldIcon className="w-5 h-5 text-slate-400" data-id="element-2806" />
            Privacy & Control
          </h2>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6" data-id="element-2807">
            <div data-id="element-2808">
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-2809">
                What Talab remembers
              </h3>
              <p className="text-sm text-slate-500 mb-4" data-id="element-2810">
                Talab uses your chat history and saved items to understand your
                preferences and improve match scores.
              </p>
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer" data-id="element-2811">
                <span className="text-sm font-medium text-slate-700" data-id="element-2812">
                  Enable AI Memory
                </span>
                <div className="relative inline-block w-10 h-6 rounded-full bg-primary-indigo" data-id="element-2813">
                  <span className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform translate-x-4" data-id="element-2814" />
                </div>
              </label>
            </div>

            <div className="pt-4 border-t border-slate-100" data-id="element-2815">
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-2816">Data Sharing</h3>
              <p className="text-sm text-slate-500 mb-4" data-id="element-2817">
                Allow Talab to share anonymous preferences with verified sellers
                to get better offers.
              </p>
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer" data-id="element-2818">
                <span className="text-sm font-medium text-slate-700" data-id="element-2819">
                  Share anonymous data
                </span>
                <div className="relative inline-block w-10 h-6 rounded-full bg-slate-300" data-id="element-2820">
                  <span className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform" data-id="element-2821" />
                </div>
              </label>
            </div>

            <div className="pt-4 border-t border-slate-100" data-id="element-2822">
              <button className="flex items-center gap-2 text-error hover:bg-error/5 px-4 py-2 rounded-lg transition-colors w-full justify-center font-medium" data-id="element-2823">
                <Trash2Icon className="w-4 h-4" data-id="element-2824" />
                Clear AI Memory
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}